import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PaginationBar, paginate } from "@/components/Pagination";
import { StockReceiveDialog } from "@/components/StockPoDialogs";
import ActivityLog from "@/components/ActivityLog";
import { Boxes, Loader2, MapPin, Plus, Search, Trash2, ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, ClipboardCheck, History } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const KINDS = [
  { k: "in", label: "Stock In", desc: "Goods received into a location", icon: ArrowDownToLine },
  { k: "out", label: "Stock Out", desc: "Goods issued or delivered", icon: ArrowUpFromLine },
  { k: "transfer", label: "Transfer", desc: "Move between locations", icon: ArrowLeftRight },
  { k: "adjust", label: "Count", desc: "Set the counted quantity", icon: ClipboardCheck },
];

function LocationsDialog({ open, onOpenChange, locations, onChanged }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/catalog/stock/locations", { name }); setName(""); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (l) => {
    try { await api.delete(`/catalog/stock/locations/${l.id}`); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="locations-dialog">
        <DialogHeader><DialogTitle>Stock Locations</DialogTitle><DialogDescription>Where you keep stock, e.g. Dar Warehouse or Site Store.</DialogDescription></DialogHeader>
        <div className="space-y-1.5">
          {locations.map((l) => (
            <div key={l.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid={`location-${l.id}`}>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#1B4383]" />{l.name}</span>
              <button onClick={() => del(l)} className="text-slate-400 hover:text-red-600" data-testid={`location-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!locations.length && <p className="text-xs text-muted-foreground">No locations yet — add your first one.</p>}
        </div>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New location name" data-testid="location-name-input" />
          <Button onClick={add} disabled={busy || !name.trim()} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="location-add-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ open, onOpenChange, row, rows, locations, onDone }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setStep(row ? 2 : 1); setF({ product_id: row?.product_id || "", kind: "", location_id: locations[0]?.id || "", to_location_id: "", qty: "", note: "" }); }
  }, [open, row, locations]);
  const prod = rows.find((r) => r.product_id === f.product_id);
  const onHand = prod?.by_location?.[f.location_id] || 0;
  const submit = async () => {
    if (f.qty === "") { toast.error("Enter a quantity"); return; }
    setBusy(true);
    try {
      await api.post("/catalog/stock/movements", { ...f, qty: Number(f.qty) });
      toast.success("Stock updated");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="move-dialog">
        <DialogHeader>
          <div className="text-[10px] font-semibold tracking-wide text-[#1B4383]" data-testid="move-step">Step {step} Of 3</div>
          <DialogTitle>{step === 1 ? "Pick The Product" : step === 2 ? "What Are You Doing?" : KINDS.find((k) => k.k === f.kind)?.label}</DialogTitle>
          <DialogDescription>{prod ? `${prod.sku} · ${prod.name}` : "Choose the SKU you're updating."}</DialogDescription>
        </DialogHeader>
        {step === 1 && (
          <Select value={f.product_id} onValueChange={(v) => { setF({ ...f, product_id: v }); setStep(2); }}>
            <SelectTrigger data-testid="move-product"><SelectValue placeholder="Select SKU" /></SelectTrigger>
            <SelectContent>{rows.map((r) => <SelectItem key={r.product_id} value={r.product_id}>{r.sku} · {r.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {step === 2 && (
          <div className="grid grid-cols-2 gap-2">
            {KINDS.map((k) => (
              <button key={k.k} type="button" onClick={() => { setF({ ...f, kind: k.k }); setStep(3); }} className="flex items-start gap-2 rounded-lg border border-border p-3 text-left transition-colors hover:border-[#1B4383] hover:bg-[#F5F8FD]" data-testid={`move-kind-${k.k}`}>
                <k.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#1B4383]" />
                <span><span className="block text-sm font-semibold text-slate-800">{k.label}</span><span className="block text-[11px] text-muted-foreground">{k.desc}</span></span>
              </button>
            ))}
          </div>
        )}
        {step === 3 && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{f.kind === "transfer" ? "From Location" : "Location"}</Label>
              <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}>
                <SelectTrigger data-testid="move-location"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground" data-testid="move-on-hand">On hand here: <b>{fmt(onHand)} {prod?.uom}</b></p>
            </div>
            {f.kind === "transfer" && (
              <div className="space-y-1.5">
                <Label className="text-xs">To Location</Label>
                <Select value={f.to_location_id} onValueChange={(v) => setF({ ...f, to_location_id: v })}>
                  <SelectTrigger data-testid="move-to-location"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{locations.filter((l) => l.id !== f.location_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">{f.kind === "adjust" ? "Counted Quantity" : "Quantity"}</Label>
              <Input inputMode="decimal" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="0" data-testid="move-qty" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Note</Label>
              <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. GRN 0042, delivered to client, stock take" data-testid="move-note" />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          {step > (row ? 2 : 1) ? <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="move-back">Back</Button> : <span />}
          {step === 3 && <Button onClick={submit} disabled={busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="move-submit">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ row, locations, onOpenChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (row) { setItems(null); api.get(`/catalog/stock/movements?product_id=${row.product_id}`).then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e))); }
  }, [row]);
  const locName = (id) => locations.find((l) => l.id === id)?.name || "—";
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="history-dialog">
        <DialogHeader><DialogTitle>Stock History</DialogTitle><DialogDescription>{row?.sku} · {row?.name}</DialogDescription></DialogHeader>
        <div className="max-h-[55vh] space-y-1.5 overflow-y-auto">
          {!items ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /> : !items.length ? <p className="py-6 text-center text-xs text-muted-foreground">No movements yet.</p> : items.map((m) => (
            <div key={m.id} className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2 text-xs" data-testid={`movement-${m.id}`}>
              <div><div className="font-semibold text-slate-800">{m.kind_label} · {locName(m.location_id)}</div><div className="text-muted-foreground">{m.note || "—"} · {m.by_name} · {new Date(m.at).toLocaleString()}</div></div>
              <span className={`font-mono font-bold ${m.qty < 0 ? "text-red-600" : "text-[#527D63]"}`}>{m.qty > 0 ? "+" : ""}{fmt(m.qty)}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StockLevels() {
  const [data, setData] = useState({ rows: [], locations: [], can_edit: false });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  const [locOpen, setLocOpen] = useState(false);
  const [move, setMove] = useState({ open: false, row: null });
  const [hist, setHist] = useState(null);
  const [recvChoices, setRecvChoices] = useState(null);
  const [recvPo, setRecvPo] = useState(null);
  const startReceive = async (r) => {
    try {
      const { data } = await api.get("/catalog/stock/orders");
      const open = data.filter((p) => ["in_transit", "partial"].includes(p.stock_status) && p.lines.some((l) => l.product_id === r.product_id && l.outstanding > 0));
      if (open.length === 1) setRecvPo(open[0]); else setRecvChoices(open);
    } catch (e) { toast.error(apiError(e)); }
  };
  const load = () => api.get("/catalog/stock/levels").then(({ data: d }) => setData(d)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search, showAll]);
  const { rows, locations, can_edit } = data;
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (showAll || r.tracked) && (!q || `${r.sku} ${r.name} ${r.category}`.toLowerCase().includes(q)));
  }, [rows, search, showAll]);
  const paged = useMemo(() => paginate(shown, page), [shown, page]);
  const tracked = rows.filter((r) => r.tracked);
  const KPIS = [
    { label: "SKUs In Stock", value: tracked.filter((r) => r.total > 0).length },
    { label: "SKUs In Transit", value: tracked.filter((r) => r.in_transit > 0).length },
    { label: "Out Of Stock", value: tracked.filter((r) => r.total <= 0).length },
    { label: "Locations", value: locations.length },
  ];
  const actions = (
    <div className="flex gap-2">
      {can_edit && <Button size="sm" variant="outline" onClick={() => setLocOpen(true)} data-testid="manage-locations-btn"><MapPin className="mr-1.5 h-4 w-4" />Locations</Button>}
      {can_edit && <Button size="sm" onClick={() => (locations.length ? setMove({ open: true, row: null }) : setLocOpen(true))} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="update-stock-btn"><Plus className="mr-1.5 h-4 w-4" />Update Stock</Button>}
    </div>
  );
  return (
    <AppLayout title="Stock Levels" actions={actions}>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="stock-kpis">
        {KPIS.map((k) => (
          <div key={k.label} className="rounded-lg border border-border bg-white p-4">
            <div className="text-[11px] font-semibold tracking-wide text-slate-400">{k.label}</div>
            <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{k.value}</div>
          </div>
        ))}
      </div>
      {can_edit && !locations.length && !loading && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#B9CBE4] bg-[#F5F8FD] px-4 py-3 text-sm text-[#1B4383]" data-testid="stock-setup-banner">
          <span><b>Step 1:</b> Add where you keep stock (e.g. Dar Warehouse), then use Update Stock to record quantities.</span>
          <Button size="sm" onClick={() => setLocOpen(true)} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="stock-setup-btn"><MapPin className="mr-1.5 h-4 w-4" />Add Location</Button>
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-white p-4">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search SKU or product…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-search" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="stock-show-all" />Show all catalog SKUs</label>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : !shown.length ? (
        <div className="rounded-lg border border-dashed border-border bg-white py-14 text-center" data-testid="stock-empty">
          <Boxes className="mx-auto mb-2 h-7 w-7 text-[#1B4383]" />
          <p className="text-sm text-muted-foreground">No stock recorded yet. {can_edit ? "Use Update Stock to add your first quantities." : ""}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-white" data-testid="stock-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-slate-50 text-left text-xs text-slate-500">
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium">Product</th>
                {locations.map((l) => <th key={l.id} className="whitespace-nowrap px-4 py-3 text-right font-medium">{l.name}</th>)}
                <th className="px-4 py-3 text-right font-medium">Total On Hand</th>
                <th className="px-4 py-3 text-right font-medium">In Transit</th>
                <th className="px-4 py-3 text-right font-medium">Committed</th>
                <th className="px-4 py-3 text-right font-medium">Available</th>
                <th className="px-4 py-3 text-right font-medium">Landed / Unit</th>
                <th className="px-4 py-3 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => (
                <tr key={r.product_id} className="border-b border-border last:border-0 hover:bg-slate-50/60" data-testid={`stock-row-${r.product_id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1B4383]">{r.sku}</td>
                  <td className="px-4 py-3 text-slate-800">{r.name}</td>
                  {locations.map((l) => <td key={l.id} className="px-4 py-3 text-right font-mono text-slate-600">{fmt(r.by_location[l.id])}</td>)}
                  <td className={`px-4 py-3 text-right font-mono font-bold ${r.total > 0 ? "text-slate-900" : "text-red-600"}`} data-testid={`stock-total-${r.product_id}`}>{fmt(r.total)} <span className="text-[10px] font-normal text-muted-foreground">{r.uom}</span></td>
                  <td className="px-4 py-3 text-right font-mono text-[#A87F31]" data-testid={`stock-transit-${r.product_id}`}>{fmt(r.in_transit)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600" data-testid={`stock-committed-${r.product_id}`}>{fmt(r.committed)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-bold ${r.available < 0 ? "text-red-600" : "text-[#527D63]"}`} data-testid={`stock-available-${r.product_id}`}>{fmt(r.available)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600" data-testid={`stock-landed-${r.product_id}`}>{r.landed_cost_usd ? `$${fmt(r.landed_cost_usd)}` : <span className="text-[11px] text-amber-600">Not set</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <button onClick={() => setHist(r)} className="mr-2 text-slate-400 hover:text-[#1B4383]" title="History" data-testid={`stock-history-${r.product_id}`}><History className="h-4 w-4" /></button>
                    {can_edit && r.in_transit > 0 && <Button size="sm" variant="outline" onClick={() => startReceive(r)} className="mr-1.5 border-[#BFD6C6] text-[#527D63]" data-testid={`stock-receive-${r.product_id}`}>Receive</Button>}
                    {can_edit && locations.length > 0 && <Button size="sm" variant="outline" onClick={() => setMove({ open: true, row: r })} data-testid={`stock-update-${r.product_id}`}>Update</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && shown.length > 0 && <PaginationBar page={page} setPage={setPage} total={shown.length} testid="stock-pagination" />}
      {!loading && <p className="mt-2 text-[11px] text-muted-foreground">Available = On Hand + In Transit − Committed (from-stock lines on open RFQs).</p>}
      <LocationsDialog open={locOpen} onOpenChange={setLocOpen} locations={locations} onChanged={load} />
      <MoveDialog open={move.open} onOpenChange={(v) => setMove((m) => ({ ...m, open: v }))} row={move.row} rows={rows} locations={locations} onDone={load} />
      <StockReceiveDialog po={recvPo} onOpenChange={(v) => !v && setRecvPo(null)} onDone={load} />
      <Dialog open={!!recvChoices} onOpenChange={(v) => !v && setRecvChoices(null)}>
        <DialogContent className="max-w-sm" data-testid="receive-choose-dialog">
          <DialogHeader><DialogTitle>Which PO Arrived?</DialogTitle><DialogDescription>This SKU is in transit on more than one stock PO.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            {(recvChoices || []).map((p) => (
              <button key={p.id} onClick={() => { setRecvChoices(null); setRecvPo(p); }} className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`receive-choose-${p.id}`}>
                <span className="font-mono font-semibold text-[#1B4383]">{p.doc_no}</span><span className="text-xs text-muted-foreground">{p.supplier_name}{p.expected_date ? ` · ${p.expected_date}` : ""}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <HistoryDialog row={hist} locations={locations} onOpenChange={(v) => !v && setHist(null)} />
      <div className="mt-6"><ActivityLog url="/catalog/history?scope=stock" refreshKey={JSON.stringify((rows || []).map((r) => r.total))} /></div>
    </AppLayout>
  );
}
